import { t } from "@lingui/macro";
import {
  type GlobalCreditAccountFragment,
  GlobalCreditAccountsDocument,
  type GlobalCreditAccountsQuery,
  type GlobalCreditAccountsQueryVariables,
  type PaginatorInfo,
} from "@src/__generated__/graphql";
import {
  LargeSelect,
  type LargeSelectProps,
} from "@src/components/ui-kit/Select/LargeSelect";
import type { IOption } from "@src/components/ui-kit/Select/types";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import mapToOptions from "@src/utils/map-to-options";
import { action, computed, makeObservable, observable } from "mobx";
import { observer, useLocalObservable } from "mobx-react-lite";

interface Props
  extends Omit<
    LargeSelectProps,
    "loadOptions" | "isLoading" | "onChange" | "isMulti"
  > {
  onChange: (
    value: any,
    selectedCreditAccount: GlobalCreditAccountFragment | undefined,
  ) => void;
  ignoreIds?: string[];
}
export const GlobalCreditAccountSelect = observer(
  function GlobalCreditAccountSelect({ ...props }: Props) {
    const state = useLocalObservable(
      () => new State(props.onChange, props.ignoreIds),
    );

    return (
      <LargeSelect
        {...props}
        placeholder={props.placeholder ?? t`Choose credit account`}
        loadOptions={state.fetchCreditAccounts}
        isLoading={state.isLoading}
        onChange={state.handleOnChange}
      />
    );
  },
);

class State {
  private fetcher = new FetchHelper<
    GlobalCreditAccountsQuery,
    GlobalCreditAccountsQueryVariables
  >(GlobalCreditAccountsDocument);

  @observable.ref
  creditAccounts: GlobalCreditAccountFragment[] = [];

  constructor(
    private onChange: Props["onChange"],
    private ignoreIds: Props["ignoreIds"],
  ) {
    makeObservable(this);
  }

  @action.bound
  async fetchCreditAccounts(
    page: number,
    perPage: number,
    searchPhrase: string,
  ): Promise<{
    data: IOption[];
    paginatorInfo: PaginatorInfo | undefined;
  }> {
    const [data, error] = await this.fetcher.fetch({
      first: perPage,
      page,
      filters: {
        search: searchPhrase,
      },
    });

    if (error || !data.globalCreditAccounts) {
      return {
        data: [],
        paginatorInfo: undefined,
      };
    }

    const filteredData =
      Array.isArray(this.ignoreIds) && this.ignoreIds.length > 0
        ? data.globalCreditAccounts.data.filter(
            (creditAccount) => !this.ignoreIds?.includes(creditAccount.id),
          )
        : data.globalCreditAccounts.data;

    this.creditAccounts = this.creditAccounts.concat(filteredData);

    return {
      data: mapToOptions.globalCreditAccounts(filteredData),
      paginatorInfo: data.globalCreditAccounts.paginatorInfo,
    };
  }

  @computed
  get isLoading() {
    return this.fetcher.isLoading.value;
  }

  @action.bound
  private getSelectedCreditAccount(
    value: IOption["value"],
  ): GlobalCreditAccountFragment | undefined {
    return this.creditAccounts.find(
      (creditAccount) => creditAccount.id === value,
    );
  }

  @action.bound
  handleOnChange(value: IOption["value"]) {
    this.onChange(value, this.getSelectedCreditAccount(value));
  }
}
